import React from "react";
import {
  PAGE, TITLE, HR, HR_LIGHT, SH,
  fmtDate, fmtPrice, coOwnerLabel, hasParty, numberToIcelandicWords,
  payLines, finLines, attachList,
  PartyBlock, Footer, SectionTitle,
} from "./printHelpers";

const PageOne = ({ form, mode }) => {
  const seller = form?.seller?.owner;
  const buyer = form?.buyer?.owner;
  const vehicle = form?.vehicle;
  const pmt = form?.payment;

  const sellerFinancing = finLines(pmt?.sellerFinancingNotes || []);
  const buyerFinancing = finLines(pmt?.buyerFinancingNotes || []);
  const docs = attachList(form?.attachments);
  const payments = payLines(pmt);

  const financingDesc = payments.length
    ? payments.join(". ")
    : pmt?.comments || "";

  const termsP1 = "Ökutækið selst í núverandi ástandi sem kaupandi hefur kynnt sér og sætt sig við, en seljandi kann að vera ábyrgur gagnvart kaupanda vegna galla samkvæmt almennum reglum. Vegna laga um neytendakaup nr. 48/2003 er bent á að bifreiðasalinn er aðeins milliliður í viðskiptunum og er ekki samábyrgur seljandanum vegna skyldna hans. Kaupandi er hvattur til að kynna sér verð á sambærilegum ökutækjum fyrir undirritun kaupsamnings! Bifreiðasali vekur athygli á því að kaupandi þarf að hafa samband við tryggingafélag sitt og ganga frá forsendum og tegund trygginga.";

  const termsP2 = "Seljandi er skráður eigandi ofangreinds ökutækis ásamt aukabúnaði þess. Hann ábyrgist að ekki hvíli veðskuldir eða eignabönd á ökutækinu umfram það sem fram kemur í kaupsamningi þessum. Seljandi greiðir sölulaun hvort sem ökutækið er selt beinni sölu, eða notað sem greiðsla við kaup á öðru ökutæki. Seljandi greiðir einnig gjald fyrir tilkynningu um eigendaskipti og útprentun úr ökutækjaskrá. Greiðslur með ávísunum, víxlum og skuldabréfum eru mótteknar með fyrirvara um að peningagreiðslur fáist í samræmi við efni þessara skjala.";

  const sigText = "Með undirritun sinni skuldbinda kaupandi sig til að kaupa og seljandi að selja ofangreint ökutæki ásamt aukabúnaði samkvæmt tilgreindum skilmálum.";
  const sigText2 = "Kaupanda ber að vátryggja ökutækið og greiða af því skatta frá kaupdegi. Þegar kaupandi hefur staðið seljanda að fullu skil á andvirði ökutækisins telst hann löglegur eigandi þess.";

  const sellerConf = "Seljandi staðfestir með undirritun sinni áreiðanleika gagna og upplýsinga um ástand ökutækisins og ber ábyrgð á að þær séu réttar og ýtarlegar.";
  const buyerConf = "Kaupandi staðfestir með undirritun sinni að fjármuna sem notaðir eru til kaupanna var aflað með lögmætum hætti og að bifreiðasali hefur kynnt honum rétt sinn til að láta óháðan aðila meta ástand ökutækisins og vakið athygli á skoðunarskyldu kaupanda.";

  const vehicleName = [vehicle?.manufacturer, vehicle?.model || vehicle?.displayName].filter(Boolean).join(" ");

  return (
    <div style={PAGE} data-testid="afsal-print-page-1">
      {mode === "electronic" && <div style={{ height: "60px" }} />}
      <h1 style={TITLE}>KAUPSAMNINGUR OG AFSAL VEGNA ÖKUTÆKJAVIÐSKIPTA</h1>
      <hr style={HR} />

      {/* SELJANDI | MEÐEIGANDI | KAUPANDI | MEÐEIGANDI */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", marginBottom: "2px" }}>
        <PartyBlock title="SELJANDI" party={seller} insurer={form?.insurance?.sellerInsurer} />
        {hasParty(form?.seller?.coOwner) ? (
          <div>
            <div style={SH}>{coOwnerLabel(form?.sellerExtraType)}</div>
            <div style={{ marginTop: "1px" }}>{form.seller.coOwner.name || ""}</div>
            <div>Kennitala {form.seller.coOwner.kennitala || ""}</div>
            <div>{form.seller.coOwner.address || ""}</div>
            <div>{[form.seller.coOwner.postalCode, form.seller.coOwner.city].filter(Boolean).join(" ")}</div>
            <div>Sími {form.seller.coOwner.phone || ""}</div>
            <div>{form.seller.coOwner.email || ""}</div>
          </div>
        ) : <div />}
        <PartyBlock title="KAUPANDI" party={buyer} insurer={form?.insurance?.buyerInsurer} />
        {hasParty(form?.buyer?.coOwner) ? (
          <div>
            <div style={SH}>{coOwnerLabel(form?.buyerExtraType)}</div>
            <div style={{ marginTop: "1px" }}>{form.buyer.coOwner.name || ""}</div>
            <div>Kennitala {form.buyer.coOwner.kennitala || ""}</div>
            <div>{form.buyer.coOwner.address || ""}</div>
            <div>{[form.buyer.coOwner.postalCode, form.buyer.coOwner.city].filter(Boolean).join(" ")}</div>
            <div>Sími {form.buyer.coOwner.phone || ""}</div>
            <div>{form.buyer.coOwner.email || ""}</div>
          </div>
        ) : <div />}
      </div>
      <hr style={HR_LIGHT} />

      {/* ÖKUTÆKI OG BÚNAÐUR */}
      <div style={{ marginBottom: "2px" }}>
        <SectionTitle>ÖKUTÆKI OG BÚNAÐUR</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "4px" }}>
          <div style={{ fontWeight: 700 }}>{vehicleName}</div>
          <div>Akstursnotkun skv. mæli {vehicle?.mileage || ""} {vehicle?.mileageUnit || "km."}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "4px" }}>
          <div>Fast númer {vehicle?.fastanumer || vehicle?.registrationNumber || ""}</div>
          <div>Fyrsti skráningardagur: {fmtDate(vehicle?.firstRegistration)}.</div>
        </div>
        <div style={{ marginTop: "8px" }}>
          <span style={{ fontWeight: 700 }}>Fylgihlutir og nánari lýsing þeirra (listinn er tæmandi og fylgir ökutækinu ekki annað en hér er tekið fram):</span>
        </div>
        <div style={{ marginTop: "4px" }}>{form?.accessories?.notes || ""}</div>
      </div>
      <hr style={HR_LIGHT} />

      {/* ÁSTAND ÖKUTÆKIS */}
      <div style={{ marginBottom: "2px" }}>
        <SectionTitle>ÁSTAND ÖKUTÆKIS</SectionTitle>
        <div style={{ marginTop: "2px" }}>☑ Ástandslýsing seljanda fylgir kaupsamningi og afsali.</div>
        <div style={{ marginTop: "2px" }}>
          <span style={{ fontWeight: 700, fontSize: "9px" }}>Annað sem ástæða þykir að taka fram um ástand ökutækisins eða ekki kemur fram við venjulega skoðun:</span>
        </div>
        <div style={{ minHeight: "12px" }}>{form?.condition?.conditionComments || form?.condition?.additionalInfo || ""}</div>
      </div>
      <hr style={HR_LIGHT} />

      {/* ÁBYRGÐIR */}
      <div style={{ marginBottom: "2px" }}>
        <SectionTitle>ÁBYRGÐIR</SectionTitle>
        <div style={{ marginTop: "1px" }}>
          <span style={{ fontWeight: 700, fontSize: "9px" }}>Aðrar ábyrgðir:</span>
        </div>
        <div style={{ minHeight: "10px" }}>{form?.warranties?.otherWarranties || ""}</div>
      </div>
      <hr style={HR_LIGHT} />

      {/* MEÐFYLGJANDI GÖGN */}
      <div style={{ marginBottom: "2px" }}>
        <SectionTitle>MEÐFYLGJANDI GÖGN</SectionTitle>
        <div style={{ minHeight: "10px", marginTop: "1px" }}>
          {docs.length ? docs.join(", ") : ""}
        </div>
      </div>
      <hr style={HR} />

      {/* AÐRIR SKILMÁLAR */}
      <div style={{ marginBottom: "2px" }}>
        <SectionTitle>AÐRIR SKILMÁLAR</SectionTitle>
        <p style={{ margin: "3px 0 4px", fontSize: "9px", lineHeight: 1.4 }}>{termsP1}</p>
        <p style={{ margin: "0 0 2px", fontSize: "9px", lineHeight: 1.4 }}>{termsP2}</p>
      </div>
      <hr style={HR_LIGHT} />

      {/* UMSAMIÐ KAUPVERÐ */}
      <div style={{ marginBottom: "2px" }}>
        <SectionTitle>UMSAMIÐ KAUPVERÐ KR.</SectionTitle>
        <hr style={{ border: "none", borderTop: "2px solid #111", margin: "4px 0" }} />
        <div style={{ display: "flex", alignItems: "baseline", gap: "16px" }}>
          <span style={{ fontSize: "22px", fontWeight: 700 }}>{fmtPrice(pmt?.purchasePrice)}</span>
          <span style={{ fontSize: "11px", fontStyle: "italic" }}>{pmt?.priceInWords || numberToIcelandicWords(pmt?.purchasePrice)}</span>
        </div>
        <hr style={{ border: "none", borderTop: "1px solid #111", margin: "4px 0" }} />
      </div>

      {/* GREIÐSLUFYRIRKOMULAG */}
      <div style={{ marginBottom: "2px" }}>
        <SectionTitle>GREIÐSLUFYRIRKOMULAG OG ATHUGASEMDIR</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", minHeight: "60px" }}>
          <div style={{ borderRight: "1px solid #ccc", paddingRight: "12px", marginTop: "4px" }}>
            <div>
              {financingDesc}
              {pmt?.comments && !payments.length ? "" : pmt?.comments ? ` ${pmt.comments}` : ""}
            </div>
            {(sellerFinancing.length > 0 || buyerFinancing.length > 0) && (
              <div style={{ marginTop: "3px", fontSize: "9px" }}>
                {sellerFinancing.length > 0 && (
                  <div>
                    <span style={{ fontWeight: 700 }}>Upplýsingar fjármögnun seljanda: </span>
                    {sellerFinancing.join(", ")}
                  </div>
                )}
                {buyerFinancing.length > 0 && (
                  <div>
                    <span style={{ fontWeight: 700 }}>Upplýsingar fjármögnun kaupanda: </span>
                    {buyerFinancing.join(", ")}
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ paddingLeft: "12px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div style={{ textAlign: "right" }}>Bifreiðagjöld eru greidd.</div>
            <div style={{ textAlign: "right" }}>Þungaskattur er greiddur.</div>
          </div>
        </div>
      </div>
      <hr style={HR} />

      {/* UNDIRRITUN */}
      <div>
        <SectionTitle>UNDIRRITUN</SectionTitle>
        <p style={{ margin: "5px 0 3px", fontSize: "9px", lineHeight: 1.4 }}>{sigText}</p>
        <p style={{ margin: "0 0 5px", fontSize: "9px", lineHeight: 1.4 }}>{sigText2}</p>

        <div style={{ marginBottom: "5px" }}>
          <span style={{ fontWeight: 700 }}>Staður og dagsetning:</span>{" "}
          {form?.general?.placeOfSale || "Reykjavík"}, {fmtDate(form?.general?.afsalDate)}.
        </div>

        <p style={{ margin: "0 0 4px", fontSize: "9px", lineHeight: 1.4, fontWeight: 700 }}>{sellerConf}</p>
        <p style={{ margin: "0 0 4px", fontSize: "9px", lineHeight: 1.4, fontWeight: 700 }}>{buyerConf}</p>

        {mode === "handwritten" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginTop: "12px" }}>
            <div>
              <div style={{ minHeight: "70px" }} />
              <div style={{ borderTop: "2px solid #111", paddingTop: "4px", minHeight: "16px" }}>
                {seller?.name || "Seljandi"}
              </div>
              <div style={{ fontSize: "8px", color: "#666" }}>Undirskrift seljanda</div>
            </div>
            <div>
              <div style={{ minHeight: "70px" }} />
              <div style={{ borderTop: "2px solid #111", paddingTop: "4px", minHeight: "16px" }}>
                {buyer?.name || "Kaupandi"}
              </div>
              <div style={{ fontSize: "8px", color: "#666" }}>Undirskrift kaupanda</div>
            </div>
            <div>
              <div style={{ minHeight: "70px" }} />
              <div style={{ borderTop: "2px solid #111", paddingTop: "4px", minHeight: "16px" }}>
                {seller?.secondarySigner?.name || ""}
              </div>
              <div style={{ fontSize: "8px", color: "#666" }}>Undirritari fyrir hönd lögaðila</div>
            </div>
            <div />
          </div>
        )}
      </div>

      <Footer createdBy={form?.general?.createdBy} refNum={form?.general?.referenceNumber} />
    </div>
  );
};

export default PageOne;
